import React, { useRef, useEffect } from 'react';
import { Star } from 'lucide-react';
import { Card } from '@/components/ui/card';

const testimonialsData = [
  {
    id: 1,
    name: 'Rajesh Kumar',
    role: 'SOC Analyst at TCS',
    image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=200',
    rating: 5,
    text: 'Indian Cyber Club Academy provided exceptional training. The hands-on approach and expert instructors helped me land my dream job in cybersecurity.'
  },
  {
    id: 2,
    name: 'Priya Sharma',
    role: 'Penetration Tester',
    image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=200',
    rating: 5,
    text: "The Bug Bounty course was game-changing. I'm now earning consistently from bug bounty programs. Best investment in my career!"
  },
  {
    id: 3,
    name: 'Amit Verma',
    role: 'Security Engineer at Infosys',
    image: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=200',
    rating: 5,
    text: 'Comprehensive curriculum and practical labs made learning effective. The placement support was excellent. Highly recommend!'
  },
  {
    id: 4,
    name: 'Sneha Patel',
    role: 'Digital Forensics Expert',
    image: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=200',
    rating: 5,
    text: 'The forensics course covered everything from basics to advanced investigation techniques. Now working with law enforcement!'
  },
  {
    id: 5,
    name: 'Vikram Singh',
    role: 'Ethical Hacker',
    image: 'https://images.unsplash.com/photo-1519345182560-3f2917c472ef?w=200',
    rating: 5,
    text: 'Best ethical hacking training in India. Real-world scenarios and expert mentorship helped me start my own consulting firm.'
  },
  {
    id: 6,
    name: 'Ananya Reddy',
    role: 'Cybersecurity Consultant',
    image: 'https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?w=200',
    rating: 5,
    text: 'The instructors are industry experts with practical experience. Learning environment is supportive and collaborative.'
  },
  {
    id: 7,
    name: 'Rohit Malhotra',
    role: 'Security Analyst at Wipro',
    image: 'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=200',
    rating: 5,
    text: 'Incredible hands-on training with real penetration testing scenarios. Got job offer even before course completion!'
  },
  {
    id: 8,
    name: 'Kavya Iyer',
    role: 'Bug Bounty Hunter',
    image: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=200',
    rating: 5,
    text: 'From zero knowledge to earning from bug bounties in just 6 months! The practical approach made all the difference.'
  }
];

const TestimonialsPage = () => {
  const scrollRef1 = useRef(null);
  const scrollRef2 = useRef(null);

  useEffect(() => {
    const scroll1 = scrollRef1.current;
    const scroll2 = scrollRef2.current;
    
    let scrollPos1 = 0;
    let scrollPos2 = 0;

    const animate1 = () => {
      if (scroll1) {
        scrollPos1 += 0.5;
        if (scrollPos1 >= scroll1.scrollWidth / 2) {
          scrollPos1 = 0;
        }
        scroll1.scrollLeft = scrollPos1;
      }
    };

    const animate2 = () => {
      if (scroll2) {
        scrollPos2 -= 0.5;
        if (scrollPos2 <= 0) {
          scrollPos2 = scroll2.scrollWidth / 2;
        }
        scroll2.scrollLeft = scrollPos2;
      }
    };

    const interval1 = setInterval(animate1, 20);
    const interval2 = setInterval(animate2, 20);

    return () => {
      clearInterval(interval1);
      clearInterval(interval2);
    };
  }, []);

  const TestimonialCard = ({ testimonial }) => (
    <Card className="flex-shrink-0 w-80 sm:w-96 bg-gray-900/50 backdrop-blur-sm border border-red-600/30 p-5 sm:p-6 hover:border-red-500/50 transition-all duration-300 hover:shadow-xl hover:shadow-red-500/10 hover:-translate-y-1 mx-3">
      <div className="flex items-center gap-1 mb-3 sm:mb-4">
        {[...Array(testimonial.rating)].map((_, i) => (
          <Star key={i} className="w-3 h-3 sm:w-4 sm:h-4 fill-yellow-500 text-yellow-500" />
        ))}
      </div>

      <p className="text-sm sm:text-base text-gray-300 mb-4 sm:mb-6 leading-relaxed break-words">
        "{testimonial.text}"
      </p>

      <div className="pt-3 sm:pt-4 border-t border-red-600/30">
        <div className="font-semibold text-sm sm:text-base text-white">{testimonial.name}</div>
        <div className="text-xs sm:text-sm text-red-500 mt-1">{testimonial.role}</div>
      </div>
    </Card>
  );

  return (
    <section className="py-16 sm:py-24 bg-black relative overflow-hidden min-h-screen pt-20 sm:pt-32">
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-red-600 rounded-full filter blur-3xl animate-pulse"></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-12 sm:mb-16">
          <div className="inline-flex items-center gap-2 mb-4">
            <Star className="w-4 h-4 sm:w-5 sm:h-5 text-red-500" />
            <span className="text-red-500 font-semibold text-xs sm:text-sm uppercase tracking-wider">
              Student Testimonials
            </span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white mt-4 mb-4 sm:mb-6 px-4">
            What Our{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-orange-500">
              Elite Hackers
            </span>{' '}
            Say
          </h2>
          <p className="text-base sm:text-lg text-gray-400 px-4">
            Trusted by <span className="text-red-500 font-bold">30,000+ students</span> who transformed their careers
          </p>
        </div>

        <div className="mb-8 relative">
          <div
            ref={scrollRef1}
            className="flex overflow-x-hidden scrollbar-hide"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            <div className="flex">
              {[...testimonialsData, ...testimonialsData].map((testimonial, index) => (
                <TestimonialCard key={`row1-${index}`} testimonial={testimonial} />
              ))}
            </div>
          </div>
        </div>

        <div className="relative">
          <div
            ref={scrollRef2}
            className="flex overflow-x-hidden scrollbar-hide"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            <div className="flex">
              {[...testimonialsData.slice().reverse(), ...testimonialsData.slice().reverse()].map((testimonial, index) => (
                <TestimonialCard key={`row2-${index}`} testimonial={testimonial} />
              ))}
            </div>
          </div>
        </div>

        <div className="mt-12 sm:mt-16 text-center">
          <div className="inline-flex flex-col sm:flex-row items-center gap-3 sm:gap-4 bg-gray-950/80 border border-red-600/30 rounded-xl px-6 sm:px-8 py-4 backdrop-blur-sm">
            <div className="flex items-center gap-2">
              <div className="flex">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 sm:w-5 sm:h-5 fill-yellow-500 text-yellow-500" />
                ))}
              </div>
              <span className="text-white font-bold text-base sm:text-lg">4.9/5</span>
            </div>
            <div className="h-px sm:h-8 w-24 sm:w-px bg-red-600/30"></div>
            <div className="text-gray-400 text-xs sm:text-sm">
              Based on <span className="text-white font-bold">5000+</span> reviews
            </div>
          </div>
        </div>
      </div>

      <div className="absolute left-0 top-0 bottom-0 w-32 bg-gradient-to-r from-black to-transparent z-10 pointer-events-none"></div>
      <div className="absolute right-0 top-0 bottom-0 w-32 bg-gradient-to-l from-black to-transparent z-10 pointer-events-none"></div>
    </section>
  );
};

export default TestimonialsPage;